import { html } from 'htm/preact';
import { useState } from '@bpmn-io/properties-panel/preact/hooks';
import { listBausteine, findCallSites, removeBaustein, getDefinitions, OPEN_EVENT } from '../utils/bausteine';

/**
 * Verzeichnis der Bausteine im Eigenschaften-Panel.
 *
 * Ohne dieses Verzeichnis wären Bausteine nur über ihre Aufrufstellen
 * erreichbar — ein Baustein ohne Aufruf wäre unsichtbar und unauffindbar,
 * obwohl er in der Datei steht.
 *
 * Zeigt je Baustein die Zahl seiner Aufrufstellen. Löschen ist nur möglich,
 * solange es keine gibt; sonst zeigten die Aufrufe ins Leere.
 */
export default function BausteinTreeGroup(element, injector) {
  let eventBus;
  try {
    eventBus = injector.get('eventBus');
  } catch { return null; }

  // NICHT canvas.getRootElement() benutzen: Das legt eine Ersatzwurzel an,
  // wenn noch keine Ebene existiert — und diese Wurzel hat kein DI. Beim
  // Speichern laeuft BpmnDiOrdering ueber alle Wurzeln und ruft
  // `getDi(root).set(...)`; die Ersatzwurzel liess das gesamte Speichern
  // scheitern ("Cannot read properties of undefined (reading 'set')").
  // Das Element, fuer das das Panel zeichnet, haengt ohnehin am Modell.
  const definitions = getDefinitions(element?.businessObject);
  if (!definitions) return null;

  const bausteine = listBausteine(definitions);
  if (bausteine.length === 0) return null;

  const BausteinListe = () => {
    const [, setTick] = useState(0);
    const neuZeichnen = () => setTick((n) => n + 1);

    const oeffnen = (baustein) => {
      if (!baustein.diagram) {
        alert(`„${baustein.name}" hat keine gespeicherte Anordnung und lässt sich darum `
          + 'nicht anzeigen. Das kommt bei Dateien vor, die ganz ohne Anordnung ankommen — '
          + 'die automatische Anordnung erfasst nur den Hauptprozess.');
        return;
      }
      eventBus.fire(OPEN_EVENT, {
        diagramId: baustein.diagram.id,
        processId: baustein.id,
        name: baustein.name,
      });
    };

    const loeschen = (baustein) => {
      const aufrufe = findCallSites(definitions, baustein.id).length;
      if (aufrufe > 0) {
        alert(`„${baustein.name}" wird noch an ${aufrufe} Stelle(n) aufgerufen und kann darum nicht entfernt werden.`);
        return;
      }
      // Das Entfernen laeuft am Kommandostapel vorbei und ist darum nicht
      // rueckgaengig zu machen. Also vorher fragen.
      const inhalt = (baustein.process.flowElements || []).length;
      if (!globalThis.confirm(
        `„${baustein.name}" mit ${inhalt} Element(en) entfernen?\n\n`
        + 'Das lässt sich nicht rückgängig machen.'
      )) return;
      removeBaustein(definitions, baustein.id);
      neuZeichnen();
    };

    return html`
      <div class="baustein-liste">
        <style>
          .baustein-liste { padding: 4px 0; }
          .baustein-zeile {
            display: flex; align-items: center; gap: 6px;
            padding: 4px 8px; border-radius: 3px;
          }
          .baustein-zeile:hover { background: #f0f4f8; }
          .baustein-name {
            flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis;
            white-space: nowrap; font-size: 13px; color: #333;
          }
          .baustein-anzahl {
            flex: 0 0 auto; font-size: 11px; color: #666;
            background: #e9ecef; border-radius: 8px; padding: 1px 7px;
          }
          .baustein-anzahl.ungenutzt { background: #fff3cd; color: #6d5e00; }
          .baustein-zeile button {
            border: none; background: transparent; cursor: pointer;
            color: #205022; font-size: 13px; padding: 2px 4px;
          }
          .baustein-zeile button:hover { text-decoration: underline; }
          .baustein-zeile button.entfernen { color: #831311; }
          .baustein-zeile button.ohne-anordnung { color: #6d5e00; }
        </style>
        <ul style="padding: 0; margin: 0; list-style: none;">
          ${bausteine.map((baustein) => {
            const aufrufe = findCallSites(definitions, baustein.id).length;
            return html`
              <li class="baustein-zeile" key=${baustein.id}>
                <span class="baustein-name" title=${baustein.id}>${baustein.name}</span>
                <span class=${`baustein-anzahl ${aufrufe === 0 ? 'ungenutzt' : ''}`}
                      title=${aufrufe === 0 ? 'Wird nirgends aufgerufen' : `${aufrufe} Aufrufstelle(n)`}>
                  ${aufrufe}×
                </span>
                <button type="button"
                        title=${baustein.diagram ? 'Baustein öffnen' : 'Ohne Anordnung — nicht anzeigbar'}
                        class=${baustein.diagram ? '' : 'ohne-anordnung'}
                        onClick=${() => oeffnen(baustein)}>
                  <i class=${baustein.diagram ? 'bi bi-box-arrow-in-right' : 'bi bi-eye-slash'}></i>
                </button>
                <button type="button" class="entfernen" title="Baustein entfernen"
                        onClick=${() => loeschen(baustein)}>
                  <i class="bi bi-trash"></i>
                </button>
              </li>
            `;
          })}
        </ul>
      </div>
    `;
  };

  return {
    id: 'bausteinTree',
    label: 'Bausteine',
    shouldOpen: true,
    entries: [{ id: 'bausteinTreeView', component: BausteinListe }],
  };
}
